import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle,
  DirectionsCar,
  Refresh,
  Schedule,
  Search,
  Visibility,
  Warning
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3992/api';

const formatarData = (data) => {
  if (!data) return '-';
  const dataObj = new Date(data);
  if (Number.isNaN(dataObj.getTime())) return '-';
  return dataObj.toLocaleDateString('pt-BR');
};

const getStatusVistoriaLabel = (status) => {
  const labels = {
    em_andamento: 'Em andamento',
    concluida: 'Concluída',
    cancelada: 'Cancelada'
  };
  return labels[status] || 'Pendente';
};

const getStatusVistoriaColor = (status) => {
  const colors = {
    em_andamento: 'warning',
    concluida: 'success',
    cancelada: 'error'
  };
  return colors[status] || 'default';
};

const getStatusAluguelColor = (status = '') => {
  const statusNormalizado = status.toLowerCase();
  if (statusNormalizado.includes('atras')) return 'error';
  if (statusNormalizado.includes('andamento') || statusNormalizado.includes('ativo')) return 'success';
  if (statusNormalizado.includes('agend')) return 'warning';
  if (statusNormalizado.includes('final')) return 'info';
  if (statusNormalizado.includes('cancel')) return 'error';
  return 'default';
};

const getDiasRestantes = (dataDevolucao) => {
  if (!dataDevolucao) return null;
  const fim = new Date(dataDevolucao);
  if (Number.isNaN(fim.getTime())) return null;
  const hoje = new Date();
  fim.setHours(0, 0, 0, 0);
  hoje.setHours(0, 0, 0, 0);
  return Math.ceil((fim - hoje) / 86400000);
};

export default function Vistorias() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [alugueis, setAlugueis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [error, setError] = useState('');
  const [filtro, setFiltro] = useState(() => searchParams.get('aluguel') || '');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [pagina, setPagina] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  const carregarAlugueis = useCallback(async ({ silencioso = false } = {}) => {
    try {
      if (silencioso) setAtualizando(true);
      else setLoading(true);

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/vistorias/alugueis-ativos`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erro ao carregar aluguéis para vistoria');
      const dados = await res.json();
      setAlugueis(Array.isArray(dados) ? dados : []);
      setUltimaAtualizacao(new Date());
      setError('');
    } catch (err) {
      setError(err.message);
      if (!silencioso) setAlugueis([]);
    } finally {
      setLoading(false);
      setAtualizando(false);
    }
  }, []);

  useEffect(() => {
    carregarAlugueis();
    const intervalo = setInterval(() => carregarAlugueis({ silencioso: true }), 15000);
    return () => clearInterval(intervalo);
  }, [carregarAlugueis]);

  useEffect(() => {
    const aluguelParam = searchParams.get('aluguel');
    if (aluguelParam) setFiltro(aluguelParam);
  }, [searchParams]);

  const alugueisFiltrados = useMemo(() => {
    const termo = filtro.trim().toLowerCase();

    return alugueis.filter((aluguel) => {
      const temVistoria = Boolean(aluguel.vistoria_id);
      if (filtroStatus === 'pendentes' && temVistoria) return false;
      if (filtroStatus === 'em_andamento' && aluguel.vistoria_status !== 'em_andamento') return false;
      if (filtroStatus === 'concluidas' && aluguel.vistoria_status !== 'concluida') return false;

      if (!termo) return true;
      return [
        aluguel.id,
        aluguel.placa,
        aluguel.marca,
        aluguel.modelo,
        aluguel.nome_cliente,
        aluguel.status_aluguel
      ].some((valor) => String(valor || '').toLowerCase().includes(termo));
    });
  }, [alugueis, filtro, filtroStatus]);

  useEffect(() => {
    setPagina(1);
  }, [filtro, filtroStatus, itensPorPagina]);

  const totalPaginas = Math.max(1, Math.ceil(alugueisFiltrados.length / itensPorPagina));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicioPagina = (paginaAtual - 1) * itensPorPagina;
  const fimPagina = inicioPagina + itensPorPagina;
  const alugueisPaginados = alugueisFiltrados.slice(inicioPagina, fimPagina);

  const totalComVistoria = alugueis.filter((a) => a.vistoria_id).length;
  const totalPendentes = alugueis.length - totalComVistoria;

  const abrirVistoria = (aluguel) => {
    if (aluguel.vistoria_id) {
      navigate(`/vistorias/${aluguel.vistoria_id}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Vistorias
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Acompanhe em tempo real os aluguéis e visualize as vistorias feitas pelo app.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={atualizando ? <CircularProgress size={16} /> : <Refresh />}
          onClick={() => carregarAlugueis({ silencioso: true })}
          disabled={atualizando}
          sx={{ alignSelf: 'flex-start' }}
        >
          Atualizar
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Aluguéis</Typography>
              <Typography variant="h5">{alugueis.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Com vistoria</Typography>
              <Typography variant="h5">{totalComVistoria}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Pendentes</Typography>
              <Typography variant="h5">{totalPendentes}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          ['todos', 'Todos'],
          ['pendentes', 'Pendentes'],
          ['em_andamento', 'Em andamento'],
          ['concluidas', 'Concluídas']
        ].map(([valor, label]) => (
          <Button
            key={valor}
            size="small"
            variant={filtroStatus === valor ? 'contained' : 'outlined'}
            onClick={() => setFiltroStatus(valor)}
          >
            {label}
          </Button>
        ))}
        {ultimaAtualizacao && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Atualizado {ultimaAtualizacao.toLocaleTimeString('pt-BR')}
          </Typography>
        )}
      </Box>

      <TextField
        fullWidth
        placeholder="Buscar por placa, cliente, veículo, status ou ID..."
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          )
        }}
      />

      {alugueisFiltrados.length === 0 ? (
        <Alert severity="info">Nenhum aluguel encontrado com estes filtros.</Alert>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {inicioPagina + 1}-{Math.min(fimPagina, alugueisFiltrados.length)} de {alugueisFiltrados.length}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Por página</Typography>
              <FormControl size="small">
                <Select
                  value={itensPorPagina}
                  onChange={(e) => setItensPorPagina(Number(e.target.value))}
                >
                  <MenuItem value={6}>6</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {alugueisPaginados.map((aluguel) => {
            const diasRestantes = getDiasRestantes(aluguel.data_devolucao);
            const temVistoria = Boolean(aluguel.vistoria_id);
            const vistoriaConcluida = aluguel.vistoria_status === 'concluida';

            return (
              <Grid item xs={12} md={6} key={aluguel.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderLeft: temVistoria
                      ? vistoriaConcluida ? '4px solid #2e7d32' : '4px solid #ed6c02'
                      : diasRestantes < 0 ? '4px solid #d32f2f' : '4px solid #1976d2'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      {temVistoria ? (
                        vistoriaConcluida ? <CheckCircle color="success" /> : <Schedule color="warning" />
                      ) : diasRestantes < 0 ? (
                        <Warning color="error" />
                      ) : (
                        <DirectionsCar color="primary" />
                      )}
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        {aluguel.marca} {aluguel.modelo}
                      </Typography>
                      <Chip
                        label={aluguel.status_aluguel || '-'}
                        color={getStatusAluguelColor(aluguel.status_aluguel)}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      <strong>Aluguel:</strong> #{aluguel.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Placa:</strong> {aluguel.placa || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Cliente:</strong> {aluguel.nome_cliente || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Retirada:</strong> {formatarData(aluguel.data_retirada)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Devolução prevista:</strong> {formatarData(aluguel.data_devolucao)}
                    </Typography>

                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Vistoria ${getStatusVistoriaLabel(aluguel.vistoria_status)}`}
                        color={getStatusVistoriaColor(aluguel.vistoria_status)}
                        size="small"
                      />
                      {diasRestantes !== null && diasRestantes < 0 && (
                        <Chip label={`${Math.abs(diasRestantes)} dia(s) em atraso`} color="error" size="small" />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      startIcon={<Visibility />}
                      onClick={() => abrirVistoria(aluguel)}
                      disabled={!temVistoria}
                      fullWidth
                    >
                      {temVistoria ? 'Ver vistoria' : 'Aguardando app'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
            })}
          </Grid>

          {totalPaginas > 1 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalPaginas}
                page={paginaAtual}
                onChange={(event, value) => setPagina(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
}
